import { useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "@/components/site/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/content";

const EVENT_TYPES = [
  "Corporate Golf Outing",
  "Nonprofit / Charity Golf Outing",
  "Private Golf Outing",
  "Memorial / Benefit Golf Outing",
  "Fundraising Golf Outing",
  "Other",
];

const SERVICES = [
  {
    title: "Marketing",
    intro: "Fairway Partners Group helps build awareness and excitement surrounding the outing.",
    items: ["Event positioning", "Event branding", "Promotional strategy", "Digital promotion", "Social media promotion", "Registration promotion", "Pre-event marketing", "Partner promotion", "Other custom marketing opportunities"],
    outro: "Our goal is to help the outing look professional, reach the right audience, and build excitement leading into event day.",
  },
  {
    title: "Sponsorships",
    intro: "Fairway Partners Group helps develop valuable sponsorship opportunities around the golf outing.",
    items: ["Sponsorship package development", "Sponsorship inventory development", "Sponsorship strategy", "Sponsor prospecting", "Sponsor outreach", "Sponsorship acquisition", "Sponsor relationship management", "Sponsor deliverable coordination", "Custom partnership opportunities", "Premium sponsorship opportunities"],
    outro: "The goal is not simply to sell logo placements. We want to create partnerships that provide real value to the organization, the outing, the golfers, and the sponsoring company.",
  },
  {
    title: "Sponsor Activations",
    intro: "Go beyond putting a company\u2019s logo on a tee sign. Fairway Partners Group can help create sponsor activations that become part of the actual golf experience.",
    items: ["Branded holes", "Sponsored contests", "Hospitality areas", "Product experiences", "Product sampling", "Giveaways", "Premium sponsor placements", "Interactive sponsor experiences", "Custom on-course activations", "Brand engagement opportunities"],
    outro: "Sponsor activations should feel like part of the outing rather than traditional advertising.",
  },
  {
    title: "Experiences",
    intro: "Fairway Partners Group helps create memorable additions that separate an outing from a standard golf tournament.",
    items: ["Specialty holes", "Golf competitions", "Entertainment", "VIP experiences", "Food and beverage experiences", "Premium golfer gifts", "Content opportunities", "Sponsor experiences", "Custom on-course experiences", "Other creative concepts developed specifically for the outing"],
    outro: "The goal is to create moments that golfers, organizations, and sponsors remember after the event is over.",
  },
];

const PARTNERS = [
  { title: "Corporations", body: "For corporate golf outings, customer appreciation, employee engagement, networking, charitable initiatives, business development, and branded golf experiences." },
  { title: "Nonprofits", body: "For nonprofit organizations using golf outings to raise funds, increase awareness, engage donors, support their mission, and strengthen relationships with corporate partners." },
  { title: "Individuals", body: "For individuals, foundations, memorial outings, benefit outings, community organizers, and other groups looking to create a professionally presented golf experience." },
];

const STEPS = [
  { n: "01", title: "Discover", body: "We learn about the organization or individual, the reason behind the outing, target audience, goals, budget, venue, timeline, and overall vision." },
  { n: "02", title: "Build", body: "We develop the appropriate marketing strategy, sponsorship opportunities, activation concepts, and experience plan around the outing." },
  { n: "03", title: "Partner", body: "We work with the client and potential sponsors to develop partnerships that make sense for the event and the brands involved." },
  { n: "04", title: "Activate", body: "We help coordinate the marketing, sponsorship activations, and experiences surrounding the event." },
  { n: "05", title: "Experience", body: "Everything comes together to create a polished golf outing designed to provide value to the organization, golfers, and sponsors." },
];

const OPTION_ITEMS = [
  "Marketing and promotional strategy",
  "Sponsorship package development",
  "Sponsorship outreach and acquisition",
  "Sponsor relationship management",
  "Sponsor activations",
  "On-course experiences",
  "Golfer experiences",
  "Event branding and positioning",
  "Custom partnership opportunities",
];

const goldInput =
  "w-full border border-fairway-gold/25 bg-black/30 px-4 py-3 text-sm text-fairway-cream placeholder:text-fairway-cream/30 outline-none transition-colors duration-300 focus:border-fairway-gold focus:ring-1 focus:ring-fairway-gold";

const blankForm = { name: "", event_type: "", phone: "", email: "", event_date: "", description: "" };

const FOverline = ({ children }) => (
  <p className="font-mono text-xs uppercase tracking-[0.3em] text-fairway-gold">{children}</p>
);

const BUDGETS = ["Under $1,500", "$1,500 – $5,000", "$5,000 – $10,000", "$10,000+", "Not sure yet"];

const blankSponsor = { business_name: "", budget: "", email: "", phone: "", contact_name: "", description: "" };

const SponsorModal = ({ open, onClose }) => {
  const [form, setForm] = useState(blankSponsor);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fairway/sponsor", form);
      setDone(true);
      setForm(blankSponsor);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-black/75 p-4 backdrop-blur-sm sm:p-8" onClick={onClose} data-testid="sponsor-modal">
      <div data-lenis-prevent onClick={(e) => e.stopPropagation()} className="max-h-[92vh] w-full max-w-xl overflow-y-auto overscroll-contain border border-fairway-gold/40 bg-fairway-deep p-6 sm:p-9">
        {done ? (
          <div className="py-10 text-center" data-testid="sponsor-success">
            <p className="font-fairway text-3xl font-semibold text-fairway-gold">Thank You.</p>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-fairway-cream/70">
              Your sponsorship inquiry has been submitted to Fairway Partners Group. Our team will review it and be in touch to discuss next steps.
            </p>
            <button onClick={onClose} data-testid="sponsor-success-close" className="mt-8 bg-fairway-gold px-7 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="sponsor-form">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fairway-gold">Fairway Partners Group</p>
                <h3 className="mt-2 font-fairway text-2xl font-semibold text-fairway-cream sm:text-3xl">Become a Sponsor</h3>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" data-testid="sponsor-close" className="text-fairway-cream/50 transition-colors hover:text-fairway-cream">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="sp-business" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Business Name *</label>
                <input id="sp-business" required value={form.business_name} onChange={set("business_name")} data-testid="sponsor-field-business" className={goldInput} placeholder="Your company" />
              </div>
              <div>
                <label htmlFor="sp-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Your Name *</label>
                <input id="sp-name" required value={form.contact_name} onChange={set("contact_name")} data-testid="sponsor-field-name" className={goldInput} placeholder="Contact person" />
              </div>
              <div>
                <label htmlFor="sp-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Email *</label>
                <input id="sp-email" required type="email" value={form.email} onChange={set("email")} data-testid="sponsor-field-email" className={goldInput} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="sp-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Phone Number *</label>
                <input id="sp-phone" required type="tel" value={form.phone} onChange={set("phone")} data-testid="sponsor-field-phone" className={goldInput} placeholder="(555) 555-5555" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sp-budget" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Budget *</label>
                <select id="sp-budget" required value={form.budget} onChange={set("budget")} data-testid="sponsor-field-budget" className={`${goldInput} appearance-none`}>
                  <option value="" disabled>Select a budget range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sp-desc" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Description *</label>
                <textarea id="sp-desc" required rows={5} value={form.description} onChange={set("description")} data-testid="sponsor-field-description" className={`${goldInput} resize-y`} placeholder="Tell us about your business, what you'd like to sponsor, and any goals for the partnership." />
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="sponsor-submit" className="group mt-7 inline-flex w-full items-center justify-center gap-3 bg-fairway-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream disabled:opacity-60 sm:w-auto">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Submitting…" : "Submit Sponsorship Inquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const FairwayForm = () => {
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fairway/inquiry", form);
      setDone(true);
      setForm(blankForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-fairway-gold/40 bg-black/30 p-10 text-center md:p-14" data-testid="fairway-success">
        <p className="font-fairway text-3xl font-semibold text-fairway-gold md:text-4xl">Thank You.</p>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-fairway-cream/70 md:text-base">
          Your information has been submitted to Fairway Partners Group. Our team will review your outing and be in touch to discuss the next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-fairway-gold/25 bg-black/30 p-6 md:p-10" data-testid="fairway-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="fw-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Name *</label>
          <input id="fw-name" required value={form.name} onChange={set("name")} data-testid="fairway-field-name" className={goldInput} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="fw-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Event Type *</label>
          <select id="fw-type" required value={form.event_type} onChange={set("event_type")} data-testid="fairway-field-event-type" className={`${goldInput} appearance-none`}>
            <option value="" disabled>Select event type</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="fw-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Phone Number *</label>
          <input id="fw-phone" required type="tel" value={form.phone} onChange={set("phone")} data-testid="fairway-field-phone" className={goldInput} placeholder="(555) 555-5555" />
        </div>
        <div>
          <label htmlFor="fw-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Email *</label>
          <input id="fw-email" required type="email" value={form.email} onChange={set("email")} data-testid="fairway-field-email" className={goldInput} placeholder="you@example.com" />
        </div>
        <div>
          <label htmlFor="fw-date" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Event Date *</label>
          <input id="fw-date" required type="date" value={form.event_date} onChange={set("event_date")} data-testid="fairway-field-event-date" className={`${goldInput} [color-scheme:dark]`} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="fw-desc" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-fairway-gold/80">Event Description *</label>
          <textarea
            id="fw-desc"
            required
            rows={6}
            value={form.description}
            onChange={set("description")}
            data-testid="fairway-field-description"
            className={`${goldInput} resize-y`}
            placeholder="Tell us about the event — its purpose, goals, expected size, sponsorship needs, and anything else you believe is relevant."
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="fairway-submit"
        className="group mt-8 inline-flex items-center gap-3 bg-fairway-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {busy ? "Submitting…" : "Submit Your Outing"}
        {!busy && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};

export default function Fairway() {
  const [sponsorOpen, setSponsorOpen] = useState(false);
  return (
    <main className="bg-fairway-deep text-fairway-cream" data-testid="fairway-page">
      {/* ---------------- Hero ---------------- */}
      <section className="relative overflow-hidden" data-testid="fairway-hero">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15 saturate-[0.6]" />
        <div className="absolute inset-0 bg-gradient-to-b from-fairway-deep/70 via-fairway-deep/85 to-fairway-deep" />
        <div className="relative mx-auto flex max-w-5xl flex-col items-center px-5 pb-24 pt-32 text-center md:pb-32 md:pt-44">
          <FadeUp>
            <img src="/fairway-logo.png" alt="Fairway Partners Group" className="mx-auto w-52 rounded-sm border border-fairway-gold/20 sm:w-64" data-testid="fairway-logo" />
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-fairway-gold" data-testid="fairway-presented-by">
              Presented by FORE the Hell of It Media
            </p>
            <h1 className="mt-6 font-fairway text-5xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl" data-testid="fairway-heading">
              Your Outing.<br /><span className="text-fairway-gold">Elevated.</span>
            </h1>
            <p className="mx-auto mt-8 max-w-2xl text-sm leading-relaxed text-fairway-cream/75 md:text-base" data-testid="fairway-hero-copy">
              Fairway Partners Group partners with corporations, nonprofits, and individuals putting together golf outings to help elevate their events through professional marketing, sponsorship development, sponsor activations, and memorable experiences.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-fairway-cream/75 md:text-base">
              We help handle the business behind the outing so our partners can create stronger events, generate more value, provide meaningful opportunities for sponsors, and deliver an experience golfers remember.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a href="#fairway-form" data-testid="fairway-cta-plan" className="group inline-flex items-center gap-3 bg-fairway-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream">
                Plan Your Outing
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a href="#fairway-services" data-testid="fairway-cta-services" className="group inline-flex items-center gap-3 border border-fairway-gold/40 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-gold transition-colors duration-300 hover:border-fairway-gold hover:bg-fairway-gold/10">
                Explore Our Services
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------------- Who We Are ---------------- */}
      <section className="border-t border-fairway-gold/15" data-testid="fairway-who">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:grid-cols-2 md:items-center md:px-8 md:py-28">
          <FadeUp>
            <FOverline>Who We Are</FOverline>
            <h2 className="mt-4 font-fairway text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl" data-testid="fairway-who-heading">
              More Than a <span className="text-fairway-gold">Golf Outing.</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-fairway-cream/75 md:text-base">
              Fairway Partners Group works alongside corporations, nonprofit organizations, and individuals putting together golf outings. Our role is not simply to organize another golf tournament.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-fairway-cream/75 md:text-base">
              We help develop and execute the marketing, sponsorship opportunities, sponsor activations, and experiences surrounding the outing.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-fairway-cream/75 md:text-base">
              Whether the goal is fundraising, corporate engagement, customer appreciation, networking, brand exposure, honoring someone, supporting a cause, or simply creating an incredible golf experience, Fairway Partners Group helps bring the pieces together.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <img src={IMAGES.walking} alt="Two golfers walking a fairway" className="w-full border border-fairway-gold/20 object-cover" data-testid="fairway-who-image" />
          </FadeUp>
        </div>
        <FadeUp>
          <div className="border-y border-fairway-gold/20 bg-fairway px-5 py-16 text-center md:py-20" data-testid="fairway-statement">
            <p className="mx-auto max-w-4xl font-fairway text-2xl font-semibold italic leading-snug text-fairway-cream sm:text-3xl md:text-4xl">
              &ldquo;You bring the reason for the outing.<br />We help turn it into an <span className="text-fairway-gold">experience.</span>&rdquo;
            </p>
          </div>
        </FadeUp>
      </section>

      {/* ---------------- What We Do ---------------- */}
      <section id="fairway-services" className="scroll-mt-24 border-t border-fairway-gold/15" data-testid="fairway-services">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <FadeUp>
            <FOverline>What We Do</FOverline>
            <div className="mt-5 h-1 w-24 bg-fairway-gold" />
            <h2 className="mt-6 max-w-3xl font-fairway text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl" data-testid="fairway-services-heading">
              The business behind the outing, <span className="text-fairway-gold">handled.</span>
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08}>
                <div className="flex h-full flex-col border border-fairway-gold/30 border-t-4 border-t-fairway-gold bg-black/35 p-7 transition-colors duration-300 hover:border-fairway-gold/60 md:p-9" data-testid={`fairway-service-${s.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fairway-gold/60">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-fairway text-2xl font-semibold text-fairway-gold md:text-3xl">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-fairway-cream/75">{s.intro}</p>
                  <ul className="mt-5 grid flex-1 gap-2 sm:grid-cols-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-[13px] leading-snug text-fairway-cream/65">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fairway-gold/70" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 border-t border-fairway-gold/15 pt-4 text-[13px] italic leading-relaxed text-fairway-cream/55">{s.outro}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Who We Partner With ---------------- */}
      <section className="border-t border-fairway-gold/15 bg-fairway" data-testid="fairway-partners">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <FadeUp>
            <FOverline>Who We Partner With</FOverline>
            <h2 className="mt-4 font-fairway text-3xl font-semibold leading-tight sm:text-4xl" data-testid="fairway-partners-heading">
              Built for every kind of outing.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PARTNERS.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.1}>
                <div className="h-full border border-fairway-gold/20 bg-black/25 p-7 md:p-8" data-testid={`fairway-partner-${p.title.toLowerCase()}`}>
                  <h3 className="font-fairway text-xl font-semibold text-fairway-gold md:text-2xl">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-fairway-cream/70">{p.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- How It Works ---------------- */}
      <section className="border-t border-fairway-gold/15" data-testid="fairway-process">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <FadeUp>
            <FOverline>How It Works</FOverline>
            <h2 className="mt-4 font-fairway text-3xl font-semibold leading-tight sm:text-4xl" data-testid="fairway-process-heading">
              Five steps to an elevated outing.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-fairway-gold/20 bg-fairway-gold/20 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s) => (
              <FadeUp key={s.n}>
                <div className="flex h-full flex-col bg-fairway-deep p-6 md:p-7" data-testid={`fairway-step-${s.title.toLowerCase()}`}>
                  <span className="font-fairway text-3xl font-semibold text-fairway-gold/40">{s.n}</span>
                  <h3 className="mt-3 font-mono text-xs font-medium uppercase tracking-[0.25em] text-fairway-gold">{s.title}</h3>
                  <p className="mt-3 text-[13px] leading-relaxed text-fairway-cream/65">{s.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Partnership Options (prominent gold band) ---------------- */}
      <section className="border-y-2 border-fairway-cream/20 bg-fairway-gold text-fairway-deep" data-testid="fairway-options">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <FadeUp>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-fairway-deep/70">Partnership Options</p>
              <h2 className="mt-4 font-fairway text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl" data-testid="fairway-options-heading">
                Fairway Partners Group Services
              </h2>
              <p className="mt-8 font-fairway text-6xl font-bold leading-none sm:text-7xl lg:text-8xl" data-testid="fairway-price">
                $1,500
              </p>
              <p className="mt-3 font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-fairway-deep/70" data-testid="fairway-price-note">
                Starting engagement level
              </p>
              <p className="mt-7 text-sm font-medium leading-relaxed text-fairway-deep/85 md:text-base">
                Every golf outing is different. Fairway Partners Group does not use rigid one-size-fits-all packages. Engagements are built around the specific needs, goals, size, and scope of each outing.
              </p>
              <p className="mt-4 text-sm font-medium leading-relaxed text-fairway-deep/85 md:text-base">
                Pricing starts at $1,500 and increases based on the size and scope of the outing, services required, sponsorship goals, marketing requirements, activation complexity, timeline, and level of Fairway Partners Group involvement.
              </p>
              <p className="mt-4 border-l-2 border-fairway-deep/40 pl-4 text-[13px] italic leading-relaxed text-fairway-deep/70">
                For engagements involving sponsorship sales or acquisition, additional commission-based compensation may apply and will be established as part of the individual agreement.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href="#fairway-form" data-testid="fairway-cta-build" className="group inline-flex items-center gap-3 bg-fairway-deep px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-gold transition-colors duration-300 hover:bg-black">
                  Build Your Outing
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <button onClick={() => setSponsorOpen(true)} data-testid="fairway-cta-sponsor" className="group inline-flex items-center gap-3 border-2 border-fairway-deep px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-deep hover:text-fairway-gold">
                  Become a Sponsor
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="border-2 border-fairway-deep/60 bg-fairway-cream p-7 shadow-[8px_8px_0_0_rgba(7,31,21,0.9)] md:p-9" data-testid="fairway-services-included">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fairway-deep/60">Services can include</p>
                <ul className="mt-5 flex flex-col gap-3">
                  {OPTION_ITEMS.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm font-medium text-fairway-deep/90">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-fairway-deep" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------------- Inquiry Form ---------------- */}
      <section id="fairway-form" className="scroll-mt-24 border-t border-fairway-gold/15" data-testid="fairway-form-section">
        <div className="mx-auto max-w-3xl px-5 py-20 md:px-8 md:py-28">
          <FadeUp>
            <FOverline>Fairway Partners Group</FOverline>
            <h2 className="mt-4 font-fairway text-3xl font-semibold leading-tight sm:text-4xl" data-testid="fairway-form-heading">
              Let&rsquo;s Talk About Your Outing
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-fairway-cream/70 md:text-base">
              Tell us a little about what you&rsquo;re planning. A member of the Fairway Partners Group team will review your information and reach out to discuss how we can help elevate your outing.
            </p>
            <div className="mt-10">
              <FairwayForm />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------------- Final CTA ---------------- */}
      <section className="relative overflow-hidden border-t border-fairway-gold/15" data-testid="fairway-final-cta">
        <img src={IMAGES.travel} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 saturate-[0.6]" />
        <div className="absolute inset-0 bg-gradient-to-r from-fairway-deep via-fairway-deep/85 to-fairway-deep/50" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-36">
          <FadeUp>
            <h2 className="max-w-4xl font-fairway text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="fairway-final-heading">
              Let&rsquo;s build an outing <span className="text-fairway-gold">people remember.</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-fairway-cream/75 md:text-base">
              Whether you&rsquo;re raising money, entertaining clients, bringing a company together, supporting a cause, honoring someone, or creating something meaningful, Fairway Partners Group can help elevate the experience.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#fairway-form" data-testid="fairway-cta-start" className="group inline-flex items-center gap-3 bg-fairway-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream">
                Start Planning Your Outing
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button onClick={() => setSponsorOpen(true)} data-testid="fairway-cta-sponsor-final" className="group inline-flex items-center gap-3 border border-fairway-gold/50 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-fairway-gold transition-colors duration-300 hover:border-fairway-gold hover:bg-fairway-gold/10">
                Become a Sponsor
              </button>
            </div>
            <p className="mt-14 font-mono text-[10px] uppercase tracking-[0.35em] text-fairway-gold/60">
              Fairway Partners Group · Presented by FORE the Hell of It Media
            </p>
          </FadeUp>
        </div>
      </section>
      <SponsorModal open={sponsorOpen} onClose={() => setSponsorOpen(false)} />
    </main>
  );
}
